import { Controller } from "@hotwired/stimulus";
import { actionSource } from "../utils/action_source";
import { syncActiveOption } from "../utils/active_option";
import { hasModifierChord, isReservedArrowChord } from "../utils/arrow_step";
import { AttributeLease } from "../utils/attribute_lease";
import { CompositionTracker } from "../utils/composition_tracker";
import { commitField } from "../utils/field_mirror";
import { ListenerSet } from "../utils/listener_set";
import { MicrotaskCoalescer } from "../utils/microtask_coalescer";
import { MoveCounter } from "../utils/move_counter";
import { scrollOptionIntoView } from "../utils/option_scroll";
import type { StateReason } from "../utils/state_reason";
import { StateRegions } from "../utils/state_regions";
import { targetSelector } from "../utils/target_selector";

/**
 * Headless, accessible combobox behavior (list autocomplete).
 *
 * Selection reflects the input and popup before notifying listeners. A synchronous
 * replacement selection suppresses the older confirmation that has not yet been sent.
 *
 * Markup contract (identifier: `stimeo--combobox`):
 *   <div data-controller="stimeo--combobox">
 *     <input type="text" role="combobox" aria-expanded="false"
 *            aria-autocomplete="list" aria-controls="listbox"
 *            data-stimeo--combobox-target="input"
 *            data-action="input->stimeo--combobox#filter
 *                         keydown->stimeo--combobox#onKeydown
 *                         focus->stimeo--combobox#open
 *                         click->stimeo--combobox#open" />
 *     <ul id="listbox" role="listbox" data-stimeo--combobox-target="list" hidden>
 *       <li role="option" id="opt-apple" data-value="apple"
 *           data-stimeo--combobox-target="option"
 *           data-action="click->stimeo--combobox#select">Apple</li>
 *       <!-- more options -->
 *     </ul>
 *     <p data-stimeo--combobox-target="empty" hidden>No fruit matches.</p>
 *   </div>
 *
 * Implements the WAI-ARIA APG **Combobox** pattern with a listbox popup and
 * list-autocomplete. Focus stays in the input; the active option is tracked with
 * `aria-activedescendant` rather than by moving DOM focus.
 *
 * `selected` dispatches `{ value: string, reason: StateReason }`.
 *
 * @remarks
 * Behavior only. Options are authored in the DOM; the controller filters them by
 * toggling each option's `hidden` attribute (case-insensitive substring match on
 * its text). The consumer owns styling, typically keyed off `[aria-selected]`.
 * When an open listbox has no matching options, the root element gets
 * `data-stimeo--combobox-empty` so the consumer can style the empty state (hide
 * the list, dim it, …) — the library imposes no visuals. The optional `empty`
 * targets are the regions that belong to that state — the "no results" message
 * the page shows in place of options, authored outside the listbox. They are
 * shown while no option matches and hidden otherwise, written wherever the state
 * is, so an authored `hidden` settles at the first reflection and a restored DOM
 * cannot leave the message standing over a listbox full of matches.
 *
 * Behavior provided:
 * - Typing filters the options and opens the listbox.
 * - Focusing or clicking the input opens the listbox, re-filtered against the
 *   current value (so re-opening with a non-matching value keeps the empty state).
 * - `ArrowDown`/`ArrowUp` move the active option (wrapping); `Enter` selects it;
 *   `Escape` closes the listbox; `Home`/`End` jump to the first/last visible
 *   option.
 * - Selecting an option fills the input (with the option's `data-value` if set,
 *   otherwise its text) and closes the listbox.
 * - A click outside the combobox closes the listbox.
 */
export class ComboboxController extends Controller<HTMLElement> {
  /** Identifies the latest published state transition. */
  readonly #moves = new MoveCounter();
  #moveToken = 0;

  static override targets = ["input", "list", "option", "empty"];
  static actions = ["close", "filter", "onKeydown", "open", "select"] as const;
  static events = ["selected"] as const;

  declare readonly emptyTargets: HTMLElement[];
  declare readonly inputTarget: HTMLInputElement;
  declare readonly inputTargets: HTMLInputElement[];
  declare readonly listTarget: HTMLElement;
  declare readonly listTargets: HTMLElement[];
  declare readonly optionTargets: HTMLElement[];
  declare readonly hasInputTarget: boolean;
  declare readonly hasListTarget: boolean;

  /** Owns `hidden` on the regions declared for the empty state. */
  readonly #emptyRegion = new StateRegions({ whenTrue: () => this.emptyTargets }, this.identifier);
  /** Stable ID of the active option; the live element is resolved before every use. */
  #activeId: string | null = null;
  #connected = false;
  /** The list the open state was last applied to. */
  #list: HTMLElement | null = null;
  /** Borrows `hidden` on the list, to give back when an element stops being the list. */
  readonly #listHidden = new AttributeLease<HTMLElement>("hidden", this.identifier);
  /** Borrows `aria-expanded` on the input, to give back when an element stops being the input. */
  readonly #expanded = new AttributeLease<HTMLElement>("aria-expanded", this.identifier);
  /** Borrows `aria-activedescendant` on the input, for the same return. */
  readonly #activeDescendant = new AttributeLease<HTMLElement>(
    "aria-activedescendant",
    this.identifier,
  );
  /** Collapses one mutation batch of target callbacks into a single pass. */
  readonly #reconcile = new MicrotaskCoalescer(() => this.#reconcileOptions());
  /**
   * Suppresses {@link ComboboxController.open | open} for the duration of the
   * programmatic re-focus in `#select`, so committing a value (which returns focus
   * to the input) does not immediately re-open the listbox via a `focus`-bound
   * action.
   */
  #suppressOpen = false;
  /** Owns IME lifecycle state; confirmed text re-filters the list once. */
  readonly #composition = new CompositionTracker({ onEnd: () => this.filter() });

  /** The listeners of one connection, released together on `disconnect()`. */
  readonly #listeners = new ListenerSet();

  /** Starts closed with no active option and registers the outside-click listener. */
  override connect(): void {
    if (this.hasInputTarget) this.#composition.observe(this.inputTarget);
    this.close();
    this.#list = this.hasListTarget ? this.listTarget : null;
    this.#listeners.add(document, "click", this.#onOutsideClick, { capture: true });
    this.#connected = true;
    this.#reconcile.activate();
  }

  /** Removes the document-level listener registered in {@link connect}. */
  override disconnect(): void {
    this.#connected = false;
    this.#reconcile.cancel();
    this.#composition.disconnect();
    this.#listeners.dispose();
  }

  /** Tracks an input added initially or after connect. */
  inputTargetConnected(input: HTMLInputElement): void {
    this.#composition.observe(input);
    if (this.#connected) this.#queueOptionReconciliation();
  }

  /**
   * Removes composition listeners, gives an input that no longer resolves as one back its
   * `aria-expanded` and `aria-activedescendant` — after `disconnect()` too, since dropping
   * the identifier leaves the element on the page — and reconciles the input that stays.
   */
  inputTargetDisconnected(input: HTMLInputElement): void {
    this.#composition.unobserve(input);
    if (!this.inputTargets.includes(input)) {
      this.#expanded.return(input);
      this.#activeDescendant.return(input);
    }
    this.#queueOptionReconciliation();
  }

  /** Applies the open state to a list that arrives after connect in front of the others. */
  listTargetConnected(): void {
    this.#adoptList();
  }

  /**
   * Applies the open state to the list left, then gives a list that no longer resolves as
   * the target its own `hidden` back — after `disconnect()` too, since dropping the
   * identifier leaves the element on the page. The open state is read off the departing
   * list first, while it still carries it.
   */
  listTargetDisconnected(list: HTMLElement): void {
    this.#adoptList();
    if (!this.listTargets.includes(list)) this.#listHidden.return(list);
  }

  /** Settles a connecting region on the side the empty state is on. */
  emptyTargetConnected(): void {
    this.#reflectEmptyState();
  }

  /** Establishes the active-state baseline, then reconciles a runtime addition. */
  optionTargetConnected(option: HTMLElement): void {
    option.setAttribute("aria-selected", "false");
    if (this.#connected) this.#queueOptionReconciliation();
  }

  /** Clears the disconnected node's active state, then reconciles the survivors. */
  optionTargetDisconnected(option: HTMLElement): void {
    option.setAttribute("aria-selected", "false");
    if (this.#connected) this.#queueOptionReconciliation();
  }

  /** Filters confirmed input text and opens the listbox. */
  filter(event?: InputEvent): void {
    if (this.#composition.isComposing(event)) return;
    this.open();
  }

  /**
   * Opens the listbox, re-filtering the options against the current input value
   * so the visible options and empty state always match what is typed (e.g.
   * re-opening with a stale non-matching value still surfaces the empty state).
   */
  open(): void {
    if (!this.hasListTarget || !this.hasInputTarget || this.#suppressOpen) return;
    this.#applyFilter();
    this.#listHidden.write(this.listTarget, null);
    this.#expanded.write(this.inputTarget, "true");
    this.#setActive(-1);
    this.#reflectEmptyState();
  }

  /**
   * Hides options that don't match the current input value (case-insensitive
   * substring). An empty query shows every option. Does not change open state.
   */
  #applyFilter(): void {
    const query = this.inputTarget.value.trim().toLowerCase();
    for (const option of this.optionTargets) {
      const text = (option.textContent ?? "").trim().toLowerCase();
      option.hidden = query.length > 0 && !text.includes(query);
    }
  }

  /**
   * Closes the listbox, clears the active option, and updates ARIA state.
   *
   * Survives a missing input in both directions. {@link inputTargetConnected}
   * accepts an input added after connect, so dereferencing it here would abort
   * `connect()` before it registers the outside-click listener, and nothing
   * re-registers it later. Symmetrically, an input removed while the popup is
   * open must still let the popup come down — so hiding the list is
   * unconditional and only the ARIA half is guarded.
   */
  close(): void {
    if (!this.hasListTarget) return;
    this.#listHidden.write(this.listTarget, "");
    this.#reflectEmptyState();
    if (this.hasInputTarget) this.#expanded.write(this.inputTarget, "false");
    this.#setActive(-1);
  }

  /** Routes keyboard interaction per the APG combobox model. */
  onKeydown(event: KeyboardEvent): void {
    // Ignore keys fired during IME composition: the `Enter` that confirms a
    // candidate (and arrows that move within it) must not select an option or
    // close the popup. Controller-owned lifecycle state covers confirming events
    // that omit the standard per-event signal.
    if (this.#composition.isComposing(event)) return;
    // A widget that already claimed the key (an enclosing composite consuming
    // arrows, a descendant editor) must not ALSO move the active option, commit
    // one, or drop the popup — composition depends on this yield.
    if (event.defaultPrevented) return;
    // A chorded arrow is the browser's, except the Alt+Down/Up this pattern
    // claims below. "Someone already consumed it" outranks it, so it is checked
    // after the yield above.
    if (isReservedArrowChord(event, ["alt"])) return;
    if ((event.key === "Home" || event.key === "End") && hasModifierChord(event)) return;
    // The two chords the pattern claims. `Alt+Down` shows the popup *without*
    // moving focus into it, and `Alt+Up` returns focus and closes — which is
    // what separates them from the bare arrows below, where the point is to
    // move the active option.
    if (event.altKey && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      // Each chord is bound in one popup state only. In the other state the
      // pattern defines nothing, so the press stays the browser's rather than
      // being swallowed on a no-op.
      if (event.key === "ArrowDown" ? !this.#isClosed : this.#isClosed) return;
      event.preventDefault();
      if (event.key === "ArrowDown") {
        this.open();
      } else {
        // The popup never holds DOM focus here (the active option is virtual, via
        // aria-activedescendant), so "return focus to the combobox" is already
        // true and closing is the whole action. Calling focus() would re-trigger
        // the focus-bound open().
        this.close();
      }
      return;
    }
    if (!this.#isClosed) this.#reconcileActive();
    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        // open() re-filters, so read the visible set afterwards.
        if (this.#isClosed) this.open();
        const visible = this.#visibleOptions();
        if (visible.length > 0) {
          const activeIndex = this.#findActiveIndex(visible);
          const next = activeIndex === -1 ? 0 : (activeIndex + 1) % visible.length;
          this.#setActive(next);
        }
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (this.#isClosed) this.open();
        const visible = this.#visibleOptions();
        if (visible.length > 0) {
          const activeIndex = this.#findActiveIndex(visible);
          // From the input (no active option) ArrowUp jumps to the last option,
          // per the APG; otherwise it wraps backwards.
          const next =
            activeIndex === -1
              ? visible.length - 1
              : (activeIndex - 1 + visible.length) % visible.length;
          this.#setActive(next);
        }
        break;
      }
      case "Home":
        if (!this.#isClosed && this.#visibleOptions().length > 0) {
          event.preventDefault();
          this.#setActive(0);
        }
        break;
      case "End": {
        const visible = this.#visibleOptions();
        if (!this.#isClosed && visible.length > 0) {
          event.preventDefault();
          this.#setActive(visible.length - 1);
        }
        break;
      }
      case "Enter": {
        const visible = this.#visibleOptions();
        const activeIndex = this.#findActiveIndex(visible);
        const active = activeIndex === -1 ? undefined : visible[activeIndex];
        if (active) {
          event.preventDefault();
          this.#select(active);
        }
        break;
      }
      case "Escape":
        // A press an inner handler already owned is yielded at the top of this
        // handler. Escape is consumed only while the list is open: with it closed
        // the press stays free for the shared resolver, so an enclosing dialog
        // still closes from a focused input.
        if (this.#isClosed) break;
        event.preventDefault();
        this.close();
        break;
      case "Tab":
        // Let focus leave naturally, but don't keep a stale popup open.
        this.close();
        break;
      default:
        break;
    }
  }

  /**
   * Closes the listbox when a click lands outside the combobox. Mirrors
   * `stimeo--menu`'s outside-click behavior; clicks on an option are inside the
   * element, so `#select` (not this handler) closes the popup after committing.
   */
  readonly #onOutsideClick = (event: MouseEvent): void => {
    if (!this.#isClosed && !this.element.contains(event.target as Node)) this.close();
  };

  /** Selects an owned option or descendant; API calls preserve external focus. */
  select(source: Event | HTMLElement): void {
    const { event, host, origin, reason } = actionSource(source);
    if (origin && origin.closest(`[data-controller~="${this.identifier}"]`) !== this.element)
      return;
    const option = host?.closest<HTMLElement>(targetSelector(this.identifier, "option"));
    if (!option || !this.optionTargets.includes(option)) return;
    const focus = event !== null || this.element.contains(document.activeElement);
    this.#select(option, reason, focus);
  }

  /**
   * Commits an option: fills the input, closes the listbox, notifies listeners.
   *
   * An input removed while the popup is open leaves the options clickable, and
   * {@link ComboboxController.close | close} already survives that state. Selection
   * does too: the popup comes down and listeners still hear the choice, with only
   * the field-bound half — the value write, the focus return, and the native
   * `change` — skipped, because there is no field to carry them.
   */
  #select(option: HTMLElement, reason: StateReason = "user", focus = true): void {
    const value = option.dataset.value ?? (option.textContent ?? "").trim();
    if (!this.hasInputTarget) {
      this.close();
      this.dispatch("selected", { detail: { value, reason } });
      return;
    }
    const changed = this.inputTarget.value !== value;
    if (changed) this.#moveToken = this.#moves.record();
    const token = this.#moveToken;
    this.inputTarget.value = value;
    this.close();
    // Returning focus to the input would re-trigger a `focus`-bound open(); guard
    // it so the listbox stays closed after a selection.
    this.#suppressOpen = true;
    if (focus && (reason !== "api" || this.element.contains(document.activeElement)))
      this.inputTarget.focus();
    this.#suppressOpen = false;
    if (!this.#moves.isLatest(token)) return;
    if (changed) {
      // A native bubbling `change` (matching <select>/listbox semantics: only on
      // an actual value change) so form-level behaviors — validation re-checks,
      // auto-submit — hear the commit without knowing this widget. Deliberately
      // NOT `input`: that is this combobox's own filter trigger and would reopen
      // the popup on every selection.
      commitField(this.inputTarget);
    }
    if (!this.#moves.isLatest(token)) return;
    this.dispatch("selected", { detail: { value, reason } });
  }

  /**
   * Writes whether the open listbox has zero matching options to the state hook
   * `data-stimeo--combobox-empty` on the root element and to the regions declared
   * for that state. Behavior only: consumers decide how to present the empty state
   * via CSS keyed off the attribute, while the `empty` targets carry the copy.
   *
   * Every path that can change the answer — opening, filtering, reconciling a
   * runtime option change, and closing — ends here, so the hook and the regions
   * are a pure function of the state rather than of the order things happened in.
   */
  #reflectEmptyState(): void {
    const empty = !this.#isClosed && this.#visibleOptions().length === 0;
    if (empty) {
      this.element.setAttribute(`data-${this.identifier}-empty`, "");
    } else {
      this.element.removeAttribute(`data-${this.identifier}-empty`);
    }
    this.#emptyRegion.reflect(this.element, empty);
  }

  /**
   * Marks the visible option at `index` active via `aria-selected` and the
   * input's `aria-activedescendant`. Pass `-1` to clear the active option.
   */
  #setActive(index: number): void {
    const visible = this.#visibleOptions();
    const active = index === -1 ? null : (visible[index] ?? null);
    // The full target set, not just the visible ones: an option hidden by
    // filtering while active would otherwise keep a stale selected state.
    syncActiveOption(this.optionTargets, active);
    this.#activeId = active?.id || null;
    if (this.hasInputTarget) this.#activeDescendant.write(this.inputTarget, active?.id || null);
    // Virtual focus never triggers the browser's native focus-scrolling, so a
    // scrollable list must follow the active option itself (list-only scroll).
    if (active && this.hasListTarget) scrollOptionIntoView(this.listTarget, active);
  }

  /**
   * Moves the open state onto the list that is now first, when that list changed. A list
   * that arrives with none before it arrives closed. Then reconciles the options against it.
   */
  #adoptList(): void {
    if (!this.#connected) return;
    const list = this.hasListTarget ? this.listTarget : null;
    const previous = this.#list;
    if (list === previous) return;
    this.#list = list;
    if (list) this.#listHidden.write(list, (previous?.hidden ?? true) ? "" : null);
    this.#queueOptionReconciliation();
  }

  /** Re-applies `aria-expanded`, filtering and active state after a target collection change. */
  #reconcileOptions(): void {
    if (this.hasInputTarget) {
      this.#expanded.write(this.inputTarget, String(!this.#isClosed));
      if (!this.#isClosed) this.#applyFilter();
    }
    this.#reconcileActive();
    this.#reflectEmptyState();
  }

  /** Preserves a surviving active ID; disappearance deliberately clears active state. */
  #reconcileActive(): void {
    if (!this.hasInputTarget || this.#isClosed) {
      this.#setActive(-1);
      return;
    }
    const visible = this.#visibleOptions();
    const index = this.#findActiveIndex(visible);
    const active = index === -1 ? null : (visible[index] ?? null);
    const selected = this.optionTargets.filter(
      (option) => option.getAttribute("aria-selected") === "true",
    );
    const idref = this.inputTarget.getAttribute("aria-activedescendant");
    const stateMatches = active
      ? this.#activeId === (active.id || null) &&
        selected.length === 1 &&
        selected[0] === active &&
        idref === (active.id || null)
      : this.#activeId === null && selected.length === 0 && idref === null;
    if (!stateMatches) this.#setActive(index);
  }

  /** Resolves the stable ID, with `aria-selected` as the ID-less compatibility marker. */
  #findActiveIndex(options: readonly HTMLElement[]): number {
    const activeId =
      (this.hasInputTarget ? this.inputTarget.getAttribute("aria-activedescendant") : null) ??
      this.#activeId;
    if (activeId) return options.findIndex((option) => option.id === activeId);
    return options.findIndex((option) => option.getAttribute("aria-selected") === "true");
  }

  /** Coalesces all target callbacks from one MutationObserver batch. */
  #queueOptionReconciliation(): void {
    this.#reconcile.schedule();
  }

  /** The options currently shown (not filtered out). */
  #visibleOptions(): HTMLElement[] {
    return this.optionTargets.filter((option) => !option.hidden);
  }

  /** Whether the listbox is currently hidden. */
  get #isClosed(): boolean {
    return !this.hasListTarget || this.listTarget.hidden !== false;
  }
}
